import { createSearchParams } from 'react-router-dom'

import type Product from '@models/Product'
import type Title from '@models/Title'
import type { URLSearchParamsInit } from 'react-router-dom'

const PageRoutes = Object.freeze({
  aboutPath: path('/company/about'),
  accountDetailsPath: path('/account#/details'),
  communityPath: path('/community'),
  developersPath: path('/developers'),
  editorPath: (id: string): string => `/scores/view/${id}`,
  giftCardPath: path('/gift_card'),
  groupPath: paramPath<number>('/groups#/groups/'),
  groupsSearchPath: queryPath('/groups_search'),
  higherEdPath: path('/highered'),
  homePath: queryPath('/home'),
  homeCollectionPath: paramPath<number>('/home/collection'),
  jobPath: path('/company/jobs'),
  learnPath: path('/learn'),
  learnTrialPath: path('/request_learn_demo'),
  legalTermsPath: path('/legal/terms'),
  legalPrivacyPath: path('/legal/privacy'),
  legalEducationPath: path('/legal/education'),
  legalCopyrightPath: path('/legal/copyright'),
  legalMarketplacePath: path('/legal/marketplace'),
  legalMusicPath: path('/legal/music'),
  logoutPath: path('/logout'),
  marketplacePath: path('/marketplace'),
  marketplaceSearchQueryPath: (query: string) => `/marketplace/search#/${query}`, // TODO: Remove path hashing
  myScoresPath: queryPath('/home/myScores'),
  newGroupPath: path('/groups#/new'),
  partnersPath: path('/partners'),
  plansPath: path('/plans'),
  premiumPath: path('/premium'),
  premiumTrialPath: path('/premium-trial'),
  privateSearchPath: queryPath('/private_search'),
  productEditing: (product: Product, openSellingDialog = false): string =>
    `/marketplace/${product.uuid}/${product.slug}/edit${
      openSellingDialog ? '?openSellingDialog=true' : ''
    }`,
  publicMembersSearchPath: queryPath('/public_members_search'),
  publicScoresSearchPath: (query: string) => `/public_scores#/search/${query}`, // TODO: Remove path hashing
  purchasingGuide: path('/purchasing-and-selling'),
  purchaseAnnual: path('/commerce/purchase_annual'),
  purchaseLearn: path('/commerce/purchase_learn'),
  purchasedScores: path('/home/yourpurchasedscores'),
  signUpPath: path('/users/sign_up'),
  siteAdminNewSitePath: path('/site/admin#/newSite'),
  siteHomePath: path('/site'),
  songCatalogPath: path('/marketplace/song_catalog'),
  soundcheckPath: path('/soundcheck'),
  teamPath: path('/company/team'),
  musicSongCatalogPath: path('/music/song_catalog'),
  musicPurchasingGuidePath: path('/purchasing-and-selling'),
  musicSellingGuidePath: path('/selling-and-purchasing'),
  musicCategoriesPath: (pathname = window.location.pathname) => `${musicUri(pathname)}/categories`,
  musicTitlePath: (uuid: string, slug?: string | null, pathname = window.location.pathname) => {
    let uri = uuid
    if (slug) uri += `/${slug}`
    return paramPath(`${musicUri(pathname)}/titles`)(uri)
  },
  musicSearchPath: (query?: URLSearchParamsInit, pathname = window.location.pathname) =>
    queryPath(`${musicUri(pathname)}/search`)(query),
  musicPath: (slug?: string | null, pathname = window.location.pathname) => {
    let uri = ''
    if (slug) uri += `/${slug}`
    return `${musicUri(pathname)}${uri}`
  },
  musicEditing: (
    title: Title,
    openSellingDialog = false,
    pathname = window.location.pathname
  ): string => {
    const path = musicUri(pathname)
    const sellingDialog = openSellingDialog ? '?openSellingDialog=true' : ''
    return `${path}/titles/${title.uuid}/${title.product?.slug}/edit${sellingDialog}`
  },
  sellingGuide: path('/selling-and-purchasing'),
  templateCopiesPath: paramPath('/home/template'),
  titleEditScorePath: (uuid: string) => `${musicUri()}/titles/edit_score/${uuid}`,
  userProfilePath: paramPath('/profile/'),
  usersGuidePath: path('/guide'),
  viewScorePath: paramPath('/scores/view/'),
  viewDiscussionPath: (forumId: number, discussionId: number) =>
    `/forums#/forum/${forumId}/discussion/${discussionId}`,
  viewGroupDiscussionPath: (groupId: number, forumId: number, discussionId: number) =>
    `/groups#/group/${groupId}/forums/${forumId}/discussion/${discussionId}`,
})

export default PageRoutes

// region Helpers
function pathWithSlash(path: string): string {
  return path.endsWith('/') ? path : `${path}/`
}

function path(path: string) {
  return () => path
}

function queryPath(path: string) {
  return (query?: URLSearchParamsInit) => `${path}${toQuery(query)}`
}

function paramPath<P = string>(path: string) {
  return (param: P) => `${pathWithSlash(path)}${param}`
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function paramQueryPath<P = string>(path: string) {
  return (param: P, query?: URLSearchParamsInit) =>
    `${pathWithSlash(path)}${param}${toQuery(query)}`
}

const LEARN_PC_PREVIEW_PATH = '/learnpremiumcontent'
export function isLearnPcPreview(pathname: string = window.location.pathname): boolean {
  return pathname.startsWith(LEARN_PC_PREVIEW_PATH)
}

function musicUri(pathname = window.location.pathname): string {
  let uri = '/music'
  if (isLearnPcPreview(pathname)) uri = `${LEARN_PC_PREVIEW_PATH}${uri}`
  return uri
}

function toQuery(query?: URLSearchParamsInit): string {
  const search = createSearchParams(query).toString()
  if (search.length) return `?${search}`
  return ''
}

export const helpers = {
  pathWithSlash,
  path,
  queryPath,
  paramPath,
  paramQueryPath,
  musicUri,
  toQuery,
}
// endregion Helpers
